import { registrarActividad } from './auditoria/servicios';
import { ApiResponse } from '../types';

/**
 * Función de alto orden que envuelve las llamadas a servicios con auditoría automática
 * 
 * @param serviceFunction Función de servicio original a auditar
 * @param accion Tipo de acción (crear, editar, consultar, etc.)
 * @param seccion Sección del sistema (empleados, clientes, etc.)
 * @returns Función envuelta que ejecuta el servicio original y registra en auditoría
 */
export function withAudit<T extends any[], R>(
  serviceFunction: (...args: T) => Promise<R>,
  accion: string,
  seccion: string
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    // La mayoría de nuestras funciones de servicio tienen token como primer argumento
    const token = args[0] as unknown as string;
    
    // Intentamos extraer userId y sucursal de los argumentos
    // Esto supone que muchos métodos tienen un formato común
    let userId = '';
    let sucursal = '';
    
    // Intentar extraer userId y sucursal del segundo argumento (típicamente un objeto de parámetros)
    if (args.length > 1 && typeof args[1] === 'object' && args[1] !== null) {
      const params = args[1] as any;
      userId = params.uid || params.userId || params.user_id || '';
      sucursal = params.sucursal || '';
    }
    
    // Crear objeto de detalles con los argumentos (excepto token que puede ser sensible)
    const detalles = {
      params: args.slice(1).map(arg => {
        // Para argumentos que son objetos, filtramos datos sensibles
        if (typeof arg === 'object' && arg !== null) {
          const filtered = { ...arg };
          // Eliminar campos sensibles
          if ('password' in filtered) delete filtered.password;
          if ('nuevaPassword' in filtered) delete filtered.nuevaPassword;
          if ('token' in filtered) delete filtered.token;
          return filtered;
        }
        return arg;
      })
    };

    try {
      // Ejecutar la función de servicio original
      const result = await serviceFunction(...args);
      
      // Si la respuesta fue exitosa, registrar en auditoría
      if ((result as any)?.success) {
        try {
          // No esperamos a que termine la auditoría para no bloquear
          registrarActividad(token, userId, accion, seccion, sucursal, detalles)
            .catch(err => console.error(`Error al registrar auditoría [${accion}][${seccion}]:`, err));
        } catch (auditError) {
          console.error(`Error al registrar auditoría [${accion}][${seccion}]:`, auditError);
        }
      }
      
      return result;
    } catch (error) {
      // Si hay un error, lo propagamos sin registrar auditoría
      throw error;
    }
  };
}

/**
 * Aplica auditoría a un objeto de servicios completo
 * @param services Objeto con funciones de servicios
 * @param seccion Sección del sistema
 * @returns Nuevo objeto con todas las funciones auditadas
 */
export function auditServices<T extends Record<string, (...args: any[]) => Promise<any>>>(
  services: T,
  seccion: string
): T {
  const auditedServices: any = {};
  
  for (const key in services) {
    if (Object.prototype.hasOwnProperty.call(services, key)) {
      const func = services[key];
      
      // Determinar acción basada en el nombre de la función
      let accion = 'desconocido';
      if (key.startsWith('get') || key.startsWith('obtener')) {
        accion = 'consultar';
      } else if (key.startsWith('create') || key.startsWith('crear')) {
        accion = 'crear';
      } else if (key.startsWith('update') || key.startsWith('actualizar')) {
        accion = 'editar';
      } else if (key.startsWith('delete') || key.startsWith('eliminar')) {
        accion = 'eliminar';
      } else if (key.startsWith('save') || key.startsWith('guardar')) {
        accion = 'guardar';
      }
      
      auditedServices[key] = withAudit(func, accion, seccion);
    }
  }
  
  return auditedServices as T;
}
